<template>
	<v-card class="pb-4">
		<v-overlay
			:model-value="loading"
			class="d-flex align-center justify-center"
			contained
			persistent
			scrim
		>
			<v-progress-circular indeterminate color="primary" size="40" />
		</v-overlay>
		<v-toolbar color="primary" dark flat>
			<v-toolbar-title>{{ title }}</v-toolbar-title>
			<v-spacer />
			<v-hover-icon class="mr-3" />
		</v-toolbar>

		<v-card-text class="pt-4">
			<!-- Read-only identity: dactyl_concept_id is sequence-minted and immutable. -->
			<div class="d-flex flex-wrap ga-2 mb-4">
				<v-chip
					v-if="dactylConceptId"
					size="small"
					color="amber-darken-2"
					variant="tonal"
				>
					<v-icon start size="20">mdi-qrcode</v-icon>
					{{ dactylConceptId }}
				</v-chip>
				<!-- One chip per rolled-up NCI concept (DCTL-2024). When a concept
				     rolls up more than one, each chip offers a "split out" action. -->
				<v-chip
					v-for="link in nciLinks"
					:key="link.nciConceptId"
					size="small"
					color="grey-darken-1"
					variant="tonal"
					:link="nciLinkable"
					:aria-label="
						nciLinkable
							? `View ${link.nciConceptId} in ${sourceLabel(link)} EVS`
							: undefined
					"
					@click="onNciClick(link)"
				>
					<v-icon start size="20">mdi-qrcode</v-icon>
					{{ sourceLabel(link) }}: {{ link.nciConceptId }}
					<v-tooltip
						v-if="canSplit"
						location="bottom"
						open-delay="300"
						text="Split into its own concept"
					>
						<template #activator="{ props }">
							<v-icon
								v-bind="props"
								end
								size="16"
								class="nci-split-icon"
								aria-label="Split out this NCI concept"
								@click.stop="confirmSplit(link)"
							>
								mdi-call-split
							</v-icon>
						</template>
					</v-tooltip>
				</v-chip>
			</div>

			<!-- Concept type / axis. Fixed once created (the hard type partition):
			     a selector while creating or adding, a read-only chip afterwards. -->
			<div class="mb-4">
				<v-select
					v-if="typeSelectable"
					v-model="conceptType"
					:items="conceptTypeOptions"
					:disabled="typeLocked"
					label="Concept type"
					:hint="
						typeLocked
							? `A ${sourceTerminology} source can only be added as ${conceptTypeLabel}.`
							: 'Normalization concepts hold scope; disease, anatomy, drug class, and mechanism of action concepts describe papers.'
					"
					persistent-hint
					density="compact"
				/>
				<div v-else class="d-flex align-center">
					<span class="text-body-2 font-weight-medium mr-2">Type</span>
					<v-chip size="small" color="blue-grey" variant="tonal">
						{{ conceptTypeLabel }}
					</v-chip>
				</div>
			</div>

			<p class="text-body-2 font-weight-medium mb-2">
				Synonyms (star marks the Dactyl term)
			</p>
			<div class="synonyms-container mb-4">
				<div
					v-for="synonym in synonyms"
					:key="synonym.uid"
					class="synonym-row mb-2"
					:class="{ 'preferred-row': synonym.uid === preferredUid }"
				>
					<div class="d-flex align-center">
						<v-tooltip location="bottom" open-delay="300">
							<template #activator="{ props }">
								<v-icon
									v-bind="props"
									class="synonym-button mr-2 flex-shrink-0"
									:color="
										synonym.uid === preferredUid
											? 'amber-darken-2'
											: 'grey-darken-1'
									"
									size="small"
									@click="togglePreferred(synonym.uid)"
								>
									{{
										synonym.uid === preferredUid
											? "mdi-star"
											: "mdi-star-outline"
									}}
								</v-icon>
							</template>
							<span>
								{{
									synonym.uid === preferredUid
										? "Dactyl term"
										: "Set as Dactyl term"
								}}
							</span>
						</v-tooltip>
						<!-- Synonyms pulled from a thesaurus are snapshot provenance and
						     read-only; only manually added synonyms are editable. -->
						<div
							v-if="synonym.source"
							class="synonym-readonly text-body-2 flex-grow-1"
							:title="synonym.synonym"
						>
							{{ synonym.synonym }}
						</div>
						<v-text-field
							v-else
							v-model="synonym.synonym"
							aria-label="Synonym"
							class="synonym-text-field flex-grow-1"
							hide-details
							density="compact"
						/>
						<v-icon
							class="synonym-button synonym-delete ml-2 flex-shrink-0"
							@click="removeSynonymRow(synonym.uid)"
						>
							mdi-delete
						</v-icon>
					</div>
					<!-- Attribution on its own line so it never constrains the synonym
					     width, and long aggregated source lists wrap cleanly. -->
					<div
						v-if="synonym.source"
						class="synonym-source text-caption text-grey-darken-2 ml-8"
					>
						{{ synonym.source }}
					</div>
				</div>
				<v-btn
					size="small"
					variant="text"
					color="secondary"
					prepend-icon="mdi-plus"
					@click="addSynonymRow"
				>
					Add synonym
				</v-btn>
			</div>

			<!-- Scope only applies to normalization concepts; the characterization
			     types (every non-normalization axis) are scope TARGETS, not holders. -->
			<template v-if="isNormalizationType">
				<div class="d-flex align-center mb-1">
					<span class="text-body-2 font-weight-medium">Scope</span>
					<QcHelpIcon
						size="small"
						:text="conceptScopeHelp"
						:learn-more-href="qcManualConceptScopeHref"
					/>
				</div>

				<v-autocomplete
					v-model="tagNames"
					:items="tagScopeOptions"
					label="Tag scope"
					hint="Tags this concept normalizes, in any tag group."
					persistent-hint
					multiple
					chips
					closable-chips
					clearable
					density="compact"
				/>

				<v-autocomplete
					v-model="tagGroupNames"
					:items="tagGroupScopeOptions"
					label="Tag group scope"
					hint="Tag groups this concept is confined to. Combined with the tag scope, a value is normalized only when both allow it."
					persistent-hint
					multiple
					chips
					closable-chips
					clearable
					density="compact"
					class="mt-3"
				/>

				<!-- Axis scope (DCTL-2025): confine this concept's suggestions to
				     papers characterized on the chosen axes. Values are characterization
				     concept DACTYL ids; an axis-only scope (no tag scope) is valid. -->
				<div class="text-body-2 font-weight-medium mt-4 mb-1">
					Characterization scope
				</div>
				<v-autocomplete
					v-for="axis in axes"
					:key="axis.key"
					v-model="axisScope[axis.key]"
					:items="axisOptions[axis.key]"
					item-title="preferredName"
					item-value="dactylConceptId"
					:custom-filter="filterConcept"
					:label="`${axis.label} papers`"
					:hint="`Only suggest for papers characterized as one of these ${axis.label.toLowerCase()} concepts.`"
					persistent-hint
					multiple
					chips
					closable-chips
					clearable
					density="compact"
					class="mt-3"
					no-data-text="No matching concepts"
				>
					<template #item="{ props, item }">
						<v-list-item v-bind="props" :title="item.raw.preferredName">
							<template #append>
								<v-chip
									size="x-small"
									color="amber-darken-2"
									variant="tonal"
									class="ml-2"
								>
									{{ item.raw.dactylConceptId }}
								</v-chip>
							</template>
						</v-list-item>
					</template>
				</v-autocomplete>
			</template>
		</v-card-text>

		<v-card-actions>
			<v-spacer />
			<v-btn
				class="bg-secondary"
				elevation="2"
				:disabled="loading"
				@click="submit"
			>
				{{ mode === "add" ? "Add" : "Save" }}
			</v-btn>
			<v-btn class="bg-white" elevation="2" @click="close"> Cancel </v-btn>
			<v-spacer />
		</v-card-actions>

		<v-dialog v-model="splitDialog" max-width="480px">
			<v-card>
				<v-card-title class="text-body-1">
					Split out this NCI concept?
				</v-card-title>
				<v-card-text class="text-body-2">
					This moves
					<strong>
						{{ splitTarget ? sourceLabel(splitTarget) : "NCI" }}
						{{ splitTarget?.nciConceptId }}
					</strong>
					<template v-if="splitTarget?.sourcePreferredName">
						({{ splitTarget.sourcePreferredName }})
					</template>
					and the synonyms it contributed into a brand-new Dactyl concept.
					Manually added synonyms stay with this one.
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn variant="text" @click="splitDialog = false"> Cancel </v-btn>
					<v-btn
						variant="flat"
						class="bg-provenance-b-tint text-provenance-b-ink"
						:loading="splitting"
						@click="performSplit"
					>
						Split out
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script>
import VHoverIcon from "@/components/VHoverIcon";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import QcHelpIcon from "@/pages/QualityControl/QcHelpIcon.vue";
import {
	resolvePreferredText,
	cleanSynonyms,
	findDuplicateSynonym,
	buildTagScope,
	seedAxisScope,
} from "./conceptEditorUtils.js";
import {
	CONCEPT_SCOPE_HELP,
	QC_MANUAL_CONCEPT_SCOPE_HREF,
} from "./conceptHelpStrings";
import {
	CONCEPT_TYPES,
	CHARACTERIZATION_AXES,
	axisLabel,
	emptyAxisArrays,
} from "@/constants/characterizationAxes";
import {
	allowedTypesForTerminology,
	codeSystemForTerminology,
} from "@/constants/sourceAxisRules";

// Concept axis/role options (derived from the shared CONCEPT_TYPES). Only
// `normalization` holds scope; the rest are characterization/scope-target types.
const CONCEPT_TYPE_OPTIONS = CONCEPT_TYPES.map((value) => ({
	value,
	title: axisLabel(value),
}));

export default {
	name: "ConceptEditor",
	components: { VHoverIcon, QcHelpIcon },

	props: {
		// "add" | "create" | "edit"
		mode: { type: String, required: true },
		// Seed for the form. Shape depends on mode:
		//  add:    { nciConceptId, preferredName, synonyms: [{ synonym, source }] }
		//  create: {}
		//  edit:   a serialized concept from /concepts/list
		initialData: { type: Object, default: () => ({}) },
		// When false, the NCI-id chip is a static badge (the caller is already
		// viewing the NCI EVS entry). When true, it links back to the EVS search.
		nciLinkable: { type: Boolean, default: true },
	},

	emits: ["dialog-close", "saved", "open-evs"],

	data() {
		return {
			loading: false,
			conceptScopeHelp: CONCEPT_SCOPE_HELP,
			qcManualConceptScopeHref: QC_MANUAL_CONCEPT_SCOPE_HREF,
			// The concept's axis/role. Seeded from initialData; editable while
			// creating/adding, immutable (read-only chip) when editing.
			conceptType: "normalization",
			// Rows carry a stable client `uid`; the preferred selection is tracked
			// by uid, never by the (mutable, two-way-bound) synonym text.
			synonyms: [],
			preferredUid: null,
			tagNames: [],
			tagGroupNames: [],
			// Tag-name options for the scope picker: the distinct tags that belong
			// to level-3 tag groups (loaded when the editor opens).
			tagScopeOptions: [],
			// Tag-group options: the names of those level-3 tag groups.
			tagGroupScopeOptions: [],
			// Characterization axes (DCTL-2025), from the shared source of truth.
			axes: CHARACTERIZATION_AXES.map((key) => ({
				key,
				label: axisLabel(key),
			})),
			// Per-axis axisScope selection: arrays of characterization concept DACTYL
			// string ids. Seeded from initialData.tagScope.axisScope.
			axisScope: emptyAxisArrays(),
			// Per-axis autocomplete options (all characterization concepts of that type).
			axisOptions: emptyAxisArrays(),
			nextUid: 0,
			// Split-out (DCTL-2024) confirm dialog state.
			splitDialog: false,
			splitTarget: null,
			splitting: false,
		};
	},

	computed: {
		title() {
			if (this.mode === "add") return "Add to Dactyl Concepts";
			if (this.mode === "create") return "New Dactyl concept";
			return "Edit concept";
		},
		dactylConceptId() {
			return this.initialData.dactylConceptId || "";
		},
		// Source concepts this Dactyl concept rolls up (DCTL-2024). In edit mode these
		// come from the serialized concept's nciLinks (each carrying its codeSystem AND
		// terminology); in add mode we synthesize a single entry from the code being
		// added, stamping the code system + terminology from the picked hit's source
		// terminology so the chip is labeled and links back correctly; local concepts
		// have none.
		nciLinks() {
			const links = this.initialData.nciLinks;
			if (Array.isArray(links) && links.length) return links;
			if (this.initialData.nciConceptId) {
				return [
					{
						nciConceptId: this.initialData.nciConceptId,
						codeSystem: codeSystemForTerminology(this.sourceTerminology),
						terminology: this.sourceTerminology || undefined,
						sourcePreferredName: null,
						isPrimary: true,
					},
				];
			}
			return [];
		},
		// The EVS terminology a picked hit came from (add mode only). Gates which axis
		// this concept may become and which code system it anchors under.
		sourceTerminology() {
			return this.initialData.sourceTerminology || null;
		},
		// Concept axis/role options. On an add from an EVS hit, restrict to the axes the
		// source terminology allows (medrt -> moa only; NCIt/NCIm -> the NCI-anchored
		// types), so the curator can't type a MED-RT MoA class as, say, a disease. The
		// backend re-validates. create/edit (no source terminology) see every type.
		conceptTypeOptions() {
			if (this.mode === "add" && this.sourceTerminology) {
				const allowed = allowedTypesForTerminology(this.sourceTerminology);
				return CONCEPT_TYPE_OPTIONS.filter((opt) =>
					allowed.includes(opt.value),
				);
			}
			return CONCEPT_TYPE_OPTIONS;
		},
		// When the source terminology allows exactly one axis (medrt -> moa), the type
		// is locked: the select is disabled and the single option is preselected.
		typeLocked() {
			return this.typeSelectable && this.conceptTypeOptions.length === 1;
		},
		// Splitting only makes sense for a saved concept rolling up 2+ source concepts.
		canSplit() {
			return this.mode === "edit" && this.nciLinks.length > 1;
		},
		// Type is chosen at creation (add / create) and frozen afterwards.
		typeSelectable() {
			return this.mode === "add" || this.mode === "create";
		},
		isNormalizationType() {
			return this.conceptType === "normalization";
		},
		conceptTypeLabel() {
			const match = CONCEPT_TYPE_OPTIONS.find(
				(opt) => opt.value === this.conceptType,
			);
			return match ? match.title : this.conceptType;
		},
	},

	created() {
		this.seedForm();
		this.loadTagScopeOptions();
		this.loadAxisOptions();
	},

	methods: {
		...mapActions(useMainStore, ["showNotification"]),

		makeRow({ synonym = "", source = null, sourceType = null }) {
			return { uid: this.nextUid++, synonym, source, sourceType };
		},
		// Match a concept option on preferred name / DACTYL id / any NCI id, so a
		// pasted id finds its concept (mirrors PaperCharacterizationEditor.filterConcept).
		filterConcept(_value, query, item) {
			const q = (query || "").trim().toLowerCase();
			if (!q) return true;
			const concept = item?.raw ?? {};
			const haystack = [
				concept.preferredName,
				concept.dactylConceptId,
				...(concept.nciConceptIds || []),
			];
			return haystack.some(
				(value) => value && String(value).toLowerCase().includes(q),
			);
		},
		// Load every characterization concept per axis as the axisScope picker options
		// (one POST /concepts/list { type } per axis, mirroring PaperCharacterizationEditor).
		async loadAxisOptions() {
			try {
				await Promise.all(
					this.axes.map((axis) =>
						this.$http
							.post("/concepts/list", { type: axis.key })
							.then(({ data }) => {
								this.axisOptions[axis.key] = data?.data || [];
							}),
					),
				);
			} catch (error) {
				// Non-fatal: the pickers degrade to the concept's existing axisScope
				// (chips still render + remove) if the roster is unavailable.
				console.error("Failed to load axis-scope concept options", error);
			}
		},
		seedForm() {
			// add: no type on the seed -> defaults to normalization (curator picks).
			// create: the originating tab's type. edit: the concept's frozen type.
			this.conceptType = this.initialData.type || "normalization";
			// On an add from an EVS hit, snap the type into the source terminology's
			// allowed set (e.g. medrt -> moa) so a restricted/locked select starts on a
			// valid axis rather than the disallowed default.
			if (this.mode === "add" && this.sourceTerminology) {
				const allowed = allowedTypesForTerminology(this.sourceTerminology);
				if (allowed.length && !allowed.includes(this.conceptType)) {
					this.conceptType = allowed[0];
				}
			}
			const rows = (this.initialData.synonyms || []).map((syn) =>
				this.makeRow({
					synonym: syn.synonym,
					source: syn.source ?? null,
					sourceType: syn.sourceType ?? null,
				}),
			);

			if (this.mode === "add" || this.mode === "edit") {
				const preferredText =
					this.mode === "edit"
						? this.editPreferred()
						: this.initialData.preferredName;

				// Ensure the preferred term is present as a row, then track it by uid.
				let preferredRow = preferredText
					? rows.find((r) => r.synonym === preferredText)
					: null;
				if (preferredText && !preferredRow) {
					preferredRow = this.makeRow({ synonym: preferredText });
					rows.unshift(preferredRow);
				}
				this.preferredUid = preferredRow ? preferredRow.uid : null;
				this.tagNames = this.initialData.tagScope?.tagNames
					? [...this.initialData.tagScope.tagNames]
					: [];
				this.tagGroupNames = this.initialData.tagScope?.tagGroupNames
					? [...this.initialData.tagScope.tagGroupNames]
					: [];
				// Guard the missing key for pre-5c.4 concepts; default each axis to [].
				// Hydrate axisScope (guards pre-5c.4 concepts that lack the key).
				this.axisScope = seedAxisScope(this.initialData.tagScope?.axisScope);
			}

			this.synonyms = rows;
			if (this.synonyms.length === 0) {
				this.addSynonymRow();
			}
		},
		// The current preferred synonym text for an edit seed.
		editPreferred() {
			const preferred = (this.initialData.synonyms || []).find(
				(s) => s.isPreferred,
			);
			return preferred ? preferred.synonym : this.initialData.preferredName;
		},
		togglePreferred(uid) {
			this.preferredUid = this.preferredUid === uid ? null : uid;
		},
		addSynonymRow() {
			this.synonyms.push(this.makeRow({}));
		},
		removeSynonymRow(uid) {
			if (uid === this.preferredUid) {
				this.preferredUid = null;
			}
			this.synonyms = this.synonyms.filter((s) => s.uid !== uid);
			if (this.synonyms.length === 0) {
				this.addSynonymRow();
			}
		},
		tagScopePayload() {
			// Only normalization concepts hold scope; a characterization concept
			// never sends one (even if a scope lingers from a type switch before save).
			if (!this.isNormalizationType) return null;
			// Delegate to the pure builder (unit-tested in conceptEditorUtils): it
			// trims/dedups, keeps only non-empty axes, and returns null only when tag,
			// group, AND axis scope are all empty (an axis-only scope persists) -
			// mirroring the backend normalizeTagScope rule.
			return buildTagScope({
				tagNames: this.tagNames,
				tagGroupNames: this.tagGroupNames,
				axisScope: this.axisScope,
			});
		},
		submit() {
			const synonyms = cleanSynonyms(this.synonyms);
			// Resolve the preferred text by uid from the (possibly edited) rows -
			// the single source of truth. It is always one of `synonyms`.
			const preferred = resolvePreferredText(this.synonyms, this.preferredUid);

			if (synonyms.length === 0) {
				this.showNotification({
					status: "error",
					message: "Add at least one synonym.",
				});
				return;
			}
			if (!preferred) {
				this.showNotification({
					status: "error",
					message: "Pick a Dactyl term (the star).",
				});
				return;
			}

			// Block a duplicate synonym before saving. Skipped in add mode, where the
			// EVS snapshot legitimately folds synonyms together (their sources
			// aggregate) - that dedup is left to the backend. In create/edit every
			// row is curator-typed or an existing distinct-fold row, so a collision
			// means the curator entered a value the concept already carries.
			if (this.mode !== "add") {
				const dup = findDuplicateSynonym(synonyms);
				if (dup) {
					this.showNotification({
						status: "error",
						message: `The synonym "${dup.duplicate}" already exists on this concept (as "${dup.existing}"). Remove the duplicate before saving.`,
					});
					return;
				}
			}

			const tagScope = this.tagScopePayload();
			let request;

			if (this.mode === "add") {
				request = this.$http.post("/concepts/add", {
					nciConceptId: this.initialData.nciConceptId,
					preferredName: preferred,
					synonyms,
					tagScope,
					type: this.conceptType,
					// The source terminology selects the anchored code system and is
					// re-validated against the type by the backend.
					terminology: this.sourceTerminology,
				});
			} else if (this.mode === "create") {
				request = this.$http.post("/concepts/create", {
					preferredName: preferred,
					synonyms,
					tagScope,
					type: this.conceptType,
				});
			} else {
				request = this.$http.post("/concepts/update", {
					id: this.initialData.id,
					preferredName: preferred,
					preferredSynonym: preferred,
					synonyms,
					tagScope,
				});
			}

			this.loading = true;
			request
				.then(() => {
					this.showNotification({
						status: "success",
						message:
							this.mode === "add"
								? "Successfully added concept"
								: "Successfully saved concept",
					});
					this.loading = false;
					this.$emit("saved");
				})
				.catch((error) => {
					const message =
						error?.response?.data?.message ??
						"A problem occurred while saving the concept";
					this.showNotification({ status: "error", message });
					this.loading = false;
				});
		},
		close() {
			this.$emit("dialog-close");
		},
		onNciClick(link) {
			if (this.nciLinkable && link?.nciConceptId) {
				this.$emit(
					"open-evs",
					link.nciConceptId,
					this.sourceTerminologyOf(link),
				);
			}
		},
		// A source link's display label + link-back terminology, by code system:
		// MED_RT (MoA classes) -> MED-RT/medrt, else NCI/ncim.
		sourceLabel(link) {
			return link?.codeSystem === "MED_RT" ? "MED-RT" : "NCI";
		},
		sourceTerminologyOf(link) {
			// Prefer the serialized terminology (distinguishes ncit from ncim, which share
			// the NCI_EVS code system); fall back to deriving from the code system.
			return (
				link?.terminology || (link?.codeSystem === "MED_RT" ? "medrt" : "ncim")
			);
		},
		confirmSplit(link) {
			this.splitTarget = link;
			this.splitDialog = true;
		},
		performSplit() {
			const link = this.splitTarget;
			if (!link) return;
			this.splitting = true;
			this.$http
				.post("/concepts/split", {
					id: this.initialData.id,
					nciConceptId: link.nciConceptId,
				})
				.then(() => {
					this.showNotification({
						status: "success",
						message: `Split ${link.nciConceptId} into its own concept`,
					});
					this.splitDialog = false;
					this.splitting = false;
					// The concept changed structurally; let the browser refetch and
					// close this editor (mirrors a save).
					this.$emit("saved");
				})
				.catch((error) => {
					const message =
						error?.response?.data?.message ??
						"A problem occurred while splitting the concept";
					this.showNotification({ status: "error", message });
					this.splitting = false;
				});
		},
		// Load the level-3 tag-group tags as scope options. Merge any tag names
		// already on the concept so a pre-existing scope always renders as a chip
		// even if a tag was later removed from its group.
		async loadTagScopeOptions() {
			const existingTags = this.initialData.tagScope?.tagNames ?? [];
			const existingGroups = this.initialData.tagScope?.tagGroupNames ?? [];
			try {
				const { data } = await this.$http.get("/tag-groups", {
					params: { level: 3, omitDeleted: true },
				});
				const names = new Set(existingTags);
				const groupNames = new Set(existingGroups);
				for (const group of data?.tagGroups ?? []) {
					if (group?.name) groupNames.add(group.name);
					for (const tag of group.tags ?? []) {
						if (tag?.name) names.add(tag.name);
					}
				}
				this.tagScopeOptions = [...names].sort((a, b) => a.localeCompare(b));
				this.tagGroupScopeOptions = [...groupNames].sort((a, b) =>
					a.localeCompare(b),
				);
			} catch (error) {
				// Non-fatal: the pickers degrade to the concept's existing scope
				// (still removable) if the roster is unavailable.
				console.error("Failed to load tag-scope options", error);
				this.tagScopeOptions = [...existingTags];
				this.tagGroupScopeOptions = [...existingGroups];
			}
		},
	},
};
</script>

<style scoped lang="scss">
// The split affordance inside an NCI chip: pointer + a small nudge so it reads
// as an action distinct from the chip's own click.
.nci-split-icon {
	cursor: pointer;
	margin-left: 2px;

	&:hover {
		opacity: 0.7;
	}
}

.synonym-button {
	border-radius: 50%;
	padding: 4px;
	transition: background-color 0.2s ease;
	cursor: pointer;

	&:hover {
		color: #f09d37;
	}
}

// The delete icon shares the button affordance but signals a destructive
// action, so it turns red (not amber) on hover. Declared after .synonym-button
// so it wins the equal-specificity :hover.
.synonym-delete:hover {
	color: rgb(var(--v-theme-error));
}

.synonyms-container {
	max-height: 360px;
	overflow-y: auto;
}

// Every row carries the same horizontal padding so the preferred-row highlight
// is a pure background change and never shifts the row's contents sideways.
.synonym-row {
	padding: 2px 4px;
	border-radius: 4px;
}

.preferred-row {
	background-color: rgba(255, 193, 7, 0.08);
}

// Read-only (thesaurus-sourced) synonym text. min-width:0 lets the flex item
// shrink so a long term wraps instead of forcing the row wider.
.synonym-readonly {
	min-width: 0;
	padding: 4px 0;
	word-break: break-word;
}

// Let the editable field shrink within its flex row rather than dictate width.
.synonym-text-field {
	min-width: 0;
}

// Attribution line: muted, wraps for long aggregated source lists.
.synonym-source {
	line-height: 1.35;
	word-break: break-word;
}
</style>
